import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, FileUp, Download, CheckCircle2, AlertCircle, AlertTriangle } from 'lucide-react';
import { api } from '@/api/client';
import { ruleCheck, type ValidatableOrder } from './OrderValidationPanel';
import { useBulkProgress, BulkProgressOverlay, UPLOAD_CHUNK_SIZE, CHUNK_TIMEOUT_MS } from './bulk-progress';
import { parseDelimited } from '@/utils/csv-helper';

interface ImportResult { created: number; skipped: number; errors: Array<{ row: number; error: string }> }

const B2C_COLUMNS = [
  'merchantId','courierId','warehouseId','orderNo','orderDate','deliveryType','customerName','customerMobile','customerEmail',
  'customerAddress','customerCity','customerState','customerPincode',
  'productName','qty','weight','chargedWeight','length','breadth','height','invoiceNo','invoiceValue','codValue','ewayBillNo','awbNo',
];
const B2B_COLUMNS = [
  'merchantId','orderNo','orderDate','courierId','warehouseId','deliveryType','insuranceType','freightCollection','pickupType',
  'customerName','customerMobile','customerAddress','customerCity','customerState','customerPincode',
  'productName','qty','totalBoxes','weight','chargedWeight','length','breadth','height',
  'invoiceNo','invoiceValue','codValue','ewayBillNo','lrNo','awbNo',
];

const B2C_SAMPLE_ROW = [
  '1','3','1','ORD-2026-001','2026-07-29','PREPAID','Anand Kumar','9876543210','anand@example.com',
  '12/A Park Street','Kolkata','West Bengal','700016',
  'Cotton Shirt','1','0.5','0.5','25','15','5','INV-2026-001','799','0','','',
];
const B2B_SAMPLE_ROW = [
  '1','ORD-B2B-2026-001','2026-07-29','42','1','PREPAID','OWNER_RISK','BILL_TO_CLIENT','FM_PICKUP',
  'M/s ABC Traders','9876543210','Plot 12, Industrial Area','Noida','Uttar Pradesh','201301',
  'Steel Coil','5','3','125.5','130','120','80','60',
  'NURB2627-1519','125000','0','EWB123456789012','','',
];

// Human column headings shown in the format panel, so the sheet's own wording maps to the CSV key.
const COLUMN_HINTS: Record<string, string> = {
  merchantId: 'Merchant ID *', orderNo: 'Order ID *', orderDate: 'Order Date (YYYY-MM-DD)',
  qty: 'Total Product Qty. *', totalBoxes: 'Total Boxes', weight: 'Actual Weight (kgs) *',
  chargedWeight: 'Total Charged Weight (kgs) *', invoiceNo: 'Invoice No. *',
  awbNo: 'Master AWB Number', lrNo: 'LR Number',
};

function buildCsv(orderType: 'B2C' | 'B2B'): string {
  const cols = orderType === 'B2C' ? B2C_COLUMNS : B2B_COLUMNS;
  const sample = orderType === 'B2C' ? B2C_SAMPLE_ROW : B2B_SAMPLE_ROW;
  const esc = (v: string) => /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  return [cols.join(','), sample.map(esc).join(',')].join('\r\n');
}

// Ops keep their own sheets whose headings are prose ("Order ID*", "Total Charged Weight(kgs)*"),
// not our CSV keys. Match those to the key the backend reads instead of importing a column of blanks.
const HEADER_ALIASES: Record<string, string> = {
  merchantid: 'merchantId', merchant: 'merchantId',
  courierid: 'courierId', courier: 'courierId',
  warehouseid: 'warehouseId', warehouse: 'warehouseId', warehousename: 'warehouseName', pickuplocation: 'warehouseName',
  orderid: 'orderNo', orderno: 'orderNo', ordernumber: 'orderNo', orderreference: 'orderNo', referenceno: 'orderNo',
  orderdate: 'orderDate', bookingdate: 'orderDate',
  deliverytype: 'deliveryType', paymentmode: 'deliveryType', paymenttype: 'deliveryType',
  insurancetype: 'insuranceType', freightcollection: 'freightCollection', freightcollectiontype: 'freightCollection',
  pickuptype: 'pickupType',
  customername: 'customerName', consigneename: 'customerName',
  customermobile: 'customerMobile', customerphone: 'customerMobile', consigneemobile: 'customerMobile', mobile: 'customerMobile',
  customeraltmobile: 'customerAltMobile', alternatemobile: 'customerAltMobile',
  customeremail: 'customerEmail', email: 'customerEmail',
  customeraddress: 'customerAddress', address: 'customerAddress',
  customercity: 'customerCity', city: 'customerCity',
  customerstate: 'customerState', state: 'customerState',
  customerpincode: 'customerPincode', pincode: 'customerPincode', destinationpincode: 'customerPincode',
  productname: 'productName', product: 'productName', itemname: 'productName',
  qty: 'qty', quantity: 'qty', totalproductqty: 'qty', totalqty: 'qty', productqty: 'qty',
  totalboxes: 'totalBoxes', boxes: 'totalBoxes', noofboxes: 'totalBoxes',
  weight: 'weight', actualweight: 'weight', deadweight: 'weight', totalweight: 'weight',
  chargedweight: 'chargedWeight', chargeableweight: 'chargedWeight', totalchargedweight: 'chargedWeight',
  length: 'length', breadth: 'breadth', width: 'breadth', height: 'height',
  invoiceno: 'invoiceNo', invoicenumber: 'invoiceNo',
  invoicevalue: 'invoiceValue', invoiceamount: 'invoiceValue', declaredvalue: 'invoiceValue',
  codvalue: 'codValue', codamount: 'codValue', collectableamount: 'codValue',
  ewaybillno: 'ewayBillNo', ewaybillnumber: 'ewayBillNo', ewaybill: 'ewayBillNo',
  awbno: 'awbNo', awb: 'awbNo', awbnumber: 'awbNo', masterawb: 'awbNo', masterawbnumber: 'awbNo', waybillno: 'awbNo',
  lrno: 'lrNo', lr: 'lrNo', lrnumber: 'lrNo',
};
/** `Total Charged Weight(kgs)*` → `totalchargedweight` → `chargedWeight`. Unknown headers pass through. */
function canonicalHeader(h: string): string {
  const squashed = h.toLowerCase().replace(/\(.*?\)/g, '').replace(/[^a-z0-9]/g, '');
  return HEADER_ALIASES[squashed] ?? h.trim();
}

/**
 * Rows from the pasted/loaded sheet, via the shared quote-aware tokeniser.
 *
 * This used to split the text into lines first and only then look for quotes, which meant a quoted
 * field containing a newline tore one record into several and shifted every later column — the
 * reason a sheet with ordinary Indian addresses ("B-225,Wing 4,S V Brindavanam …") failed to
 * upload. `parseDelimited` reads the whole text in one pass, so a quoted field may hold commas,
 * quotes and line breaks; it also strips Excel's BOM and auto-detects tab/semicolon separators, so
 * cells pasted straight out of Excel work as well as a saved .csv.
 */
function parseCsv(text: string): Array<Record<string, string>> {
  return parseDelimited(text, { mapHeader: canonicalHeader }).rows;
}

function rowToValidatable(row: Record<string, string>): ValidatableOrder {
  const num = (v: any) => { const n = Number(v); return Number.isFinite(n) && v !== '' ? n : undefined; };
  return {
    customerName: row.customerName, customerMobile: row.customerMobile, customerAddress: row.customerAddress,
    customerCity: row.customerCity, customerState: row.customerState, customerPincode: row.customerPincode,
    // Upload rows carry a per-row `weight` column; expose it as totalWeight too so the B2B
    // order-level weight check (which has no per-box weight) is satisfied.
    totalWeight: num(row.weight),
    dimensions: [{ length: num(row.length), breadth: num(row.breadth), height: num(row.height), weight: num(row.weight) }],
  };
}

export function OrderUploadDialog({ orderType, onClose }: { orderType: 'B2C' | 'B2B'; onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [result, setResult] = useState<ImportResult | null>(null);

  // Client-side rule validation (the "pattern" check) — runs live on the pasted/loaded CSV.
  const clientReport = useMemo(() => {
    const rows = parseCsv(text);
    const errs: Array<{ row: number; msgs: string[] }> = [];
    const warns: Array<{ row: number; msgs: string[] }> = [];
    rows.forEach((r, i) => {
      const issues = ruleCheck(orderType, rowToValidatable(r));
      const e = issues.filter(x => x.severity === 'error').map(x => x.message);
      const w = issues.filter(x => x.severity === 'warning').map(x => x.message);
      if (e.length) errs.push({ row: i + 2, msgs: e });
      if (w.length) warns.push({ row: i + 2, msgs: w });
    });
    return { count: rows.length, errs, warns };
  }, [text, orderType]);
  const hasClientErrors = clientReport.errs.length > 0;

  // A big sheet is sent in chunks with live progress rather than as one long request: a few thousand
  // rows outlives the client's 30s timeout, and the old behaviour — a spinner, then "Upload failed"
  // while the server was still importing — had people uploading the same file two and three times.
  // See ./bulk-progress.
  const bulkProgress = useBulkProgress();
  const runUpload = async (rows: Array<Record<string, any>>, dryRun: boolean) => {
    let created = 0, skipped = 0;
    const errors: ImportResult['errors'] = [];
    await bulkProgress.run<Record<string, any>, ImportResult>({
      label: dryRun ? 'Validating' : 'Importing',
      items: rows,
      chunkSize: UPLOAD_CHUNK_SIZE,
      call: (chunk) =>
        api.post<ImportResult>('/orders/upload', { orderType, rows: chunk, dryRun }, { timeout: CHUNK_TIMEOUT_MS })
          .then((r) => r.data),
      tally: (r) => ({ ok: r?.created ?? 0, failed: r?.errors?.length ?? 0 }),
      onChunk: (r, _chunk, startIndex) => {
        created += r?.created ?? 0;
        skipped += r?.skipped ?? 0;
        // The server numbers rows WITHIN the chunk it was given (`i + 2`), so shift them back onto the
        // real CSV line before merging — otherwise every chunk reports errors on "row 2, row 3, …" and
        // the Download-errors file pulls the wrong rows out of the sheet.
        for (const e of r?.errors ?? []) errors.push({ ...e, row: e.row + startIndex });
      },
      onChunkError: (err, chunk, startIndex) => {
        const msg = err?.response?.data?.message ?? err?.message ?? 'Upload failed';
        if (errors.length === 0) toast.error(msg);
        chunk.forEach((_r, i) => errors.push({ row: startIndex + i + 2, error: msg }));
      },
    });
    setResult({ created, skipped, errors });
    if (!dryRun) {
      // Refresh BOTH the orders list AND the tab counts so the new rows + counter pills update without a page refresh
      qc.invalidateQueries({ predicate: (q: any) => Array.isArray(q.queryKey) && (q.queryKey[0] === 'orders' || q.queryKey[0] === 'order-tab-counts') });
      if (created > 0) toast.success(`Imported ${created} order(s)`);
      if (errors.length > 0) toast.error(`${errors.length} row(s) could not be imported`);
    }
  };

  const downloadTemplate = () => {
    const csv = buildCsv(orderType);
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url;
    a.download = `orders-${orderType.toLowerCase()}-template.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader();
    r.onload = (ev) => setText(String(ev.target?.result ?? ''));
    r.readAsText(file);
  };

  const [lastRows, setLastRows] = useState<Array<Record<string, any>>>([]);

  const run = (dryRun: boolean) => {
    const rows = parseCsv(text);
    if (rows.length === 0) { toast.error('No rows to upload'); return; }
    setLastRows(rows);
    void runUpload(rows, dryRun);
  };

  const downloadErrors = () => {
    if (!result?.errors?.length) return;
    const headers = Object.keys(lastRows[0] ?? {});
    const cols = ['row', 'error', ...headers];
    const esc = (v: any) => { if (v == null) return ''; const ss = String(v); return /[",\n]/.test(ss) ? `"${ss.replace(/"/g, '""')}"` : ss; };
    const lines = [cols.join(',')];
    for (const err of result.errors) {
      const idx = err.row - 2; // CSV row -> data row index
      const row = lastRows[idx] ?? {};
      lines.push([esc(err.row), esc(err.error), ...headers.map(h => esc(row[h]))].join(','));
    }
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `upload-errors-${orderType.toLowerCase()}-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(a.href);
  };

  const COLUMNS = orderType === 'B2C' ? B2C_COLUMNS : B2B_COLUMNS;

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 space-y-1">
        <div className="font-medium">CSV format — {orderType} ({COLUMNS.length} columns)</div>
        <div className="font-mono text-[10px] break-all">{COLUMNS.join(', ')}</div>
        <div className="mt-1.5 text-[10px] text-blue-800">
          Your sheet's own headings are matched automatically — <em>{COLUMNS.filter(c => COLUMN_HINTS[c]).map(c => `${COLUMN_HINTS[c]} → ${c}`).join(' · ')}</em>
        </div>
        <div className="mt-2 text-blue-800">
          <strong>Required:</strong> merchantId, customerName, customerMobile (10 digits), customerAddress, customerCity, customerState, customerPincode (6 digits).
          <strong className="ml-2">deliveryType:</strong> COD or PREPAID (default PREPAID).
          <strong className="ml-2">warehouseId:</strong> optional (pickup warehouse).
          <strong className="ml-2">orderDate:</strong> optional — YYYY-MM-DD, blank means today.
          <strong className="ml-2">chargedWeight:</strong> optional — the weight to bill on; blank falls back to weight.
          {orderType === 'B2C'
            ? <><strong className="ml-2">awbNo:</strong> optional — a pre-assigned AWB. If provided, the order is imported as Booking Received and the courier is NOT called.</>
            : <><strong className="ml-2">lrNo / awbNo:</strong> optional — a pre-assigned LR and Master AWB. If provided, the order is imported as Booking Received and the courier is NOT called. <strong className="ml-2">insuranceType:</strong> OWNER_RISK / CARRIER_RISK. <strong className="ml-2">freightCollection:</strong> BILL_TO_CLIENT / COD. <strong className="ml-2">pickupType:</strong> FM_PICKUP / SELF_DROP.</>}
        </div>
        <button type="button" onClick={downloadTemplate}
          className="mt-2 px-3 py-1.5 text-xs bg-primary text-white rounded hover:bg-primary-700 flex items-center gap-1.5">
          <Download size={12} /> Download {orderType} sample template (CSV)
        </button>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Upload CSV file</label>
        <input type="file" accept=".csv,text/csv" onChange={handleFile}
          className="text-sm w-full file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:text-sm file:bg-primary file:text-white file:cursor-pointer cursor-pointer" />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Or paste CSV content</label>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6}
          placeholder={`${COLUMNS.join(',')}\n...`} className="input font-mono text-xs" />
      </div>

      {text.trim() && (clientReport.errs.length > 0 || clientReport.warns.length > 0) && (
        <div className={`rounded-lg border p-3 text-xs space-y-1.5 ${hasClientErrors ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className={`font-medium flex items-center gap-1.5 ${hasClientErrors ? 'text-rose-800' : 'text-amber-800'}`}>
            {hasClientErrors ? <AlertCircle size={14} /> : <AlertTriangle size={14} />}
            Validation: {clientReport.errs.length} row(s) with errors, {clientReport.warns.length} with warnings (of {clientReport.count})
          </div>
          <ul className="max-h-36 overflow-y-auto space-y-0.5">
            {clientReport.errs.slice(0, 8).map((e, i) => (
              <li key={`e${i}`} className="text-rose-700"><b>Row {e.row}:</b> {e.msgs.join(' ')}</li>
            ))}
            {clientReport.warns.slice(0, 6).map((w, i) => (
              <li key={`w${i}`} className="text-amber-700"><b>Row {w.row}:</b> {w.msgs.join(' ')}</li>
            ))}
          </ul>
          {hasClientErrors && <div className="text-[11px] text-rose-700 pt-1 border-t border-rose-200">Rows with errors must be fixed before uploading. Warnings won't block the upload.</div>}
        </div>
      )}

      {result && (
        <div className="rounded-lg border">
          <div className="grid grid-cols-3 divide-x divide-slate-200 text-center bg-white border-b border-slate-200">
            <div className="p-3"><div className="text-xs text-slate-500">Total Rows</div><div className="text-2xl font-bold text-slate-900">{result.created + result.skipped}</div></div>
            <div className="p-3 bg-emerald-50"><div className="text-xs text-emerald-700">Success</div><div className="text-2xl font-bold text-emerald-700">{result.created}</div></div>
            <div className="p-3 bg-rose-50"><div className="text-xs text-rose-700">Failed</div><div className="text-2xl font-bold text-rose-700">{result.skipped}</div></div>
          </div>
          {result.errors.length > 0 && (
            <div className="p-3 bg-amber-50">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-amber-900 flex items-center gap-1.5"><AlertCircle size={14} /> {result.errors.length} row(s) failed</div>
                <button onClick={downloadErrors} className="text-xs px-3 py-1.5 bg-rose-600 text-white rounded hover:bg-rose-700 flex items-center gap-1">
                  <Download size={12} /> Download Errors (CSV)
                </button>
              </div>
              <ul className="text-xs text-amber-900 max-h-32 overflow-y-auto space-y-0.5">
                {result.errors.slice(0, 10).map((e, i) => <li key={i}><b>Row {e.row}:</b> {e.error}</li>)}
                {result.errors.length > 10 && <li className="italic">… +{result.errors.length - 10} more — use the Download button to see all</li>}
              </ul>
            </div>
          )}
          {result.errors.length === 0 && (
            <div className="p-3 bg-emerald-50 flex items-center gap-2 text-sm text-emerald-800">
              <CheckCircle2 size={16} className="text-emerald-600" /> All rows imported successfully.
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
        <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Close</button>
        <button type="button" onClick={() => run(true)} disabled={bulkProgress.running || !text.trim()}
          className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-60 flex items-center gap-2">
          {bulkProgress.running && <Loader2 size={14} className="animate-spin" />}
          Validate Only
        </button>
        <button type="button" onClick={() => run(false)} disabled={bulkProgress.running || !text.trim() || hasClientErrors}
          title={hasClientErrors ? 'Fix the validation errors above before uploading' : undefined}
          className="px-4 py-2 bg-primary text-white text-sm rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2">
          {bulkProgress.running ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />}
          Upload Orders
        </button>
      </div>
      {bulkProgress.progress && <BulkProgressOverlay progress={bulkProgress.progress} />}
    </div>
  );
}
